import { useState, useMemo, useCallback, useEffect } from "react"
import { Alert, Platform, Linking } from "~/ui/primitives"
import * as FileSystem from "~/ui/expo-file-system"
import * as MediaLibrary from "~/ui/expo-media-library"
import * as Application from "expo-application"
import * as IntentLauncher from "expo-intent-launcher"
import { CameraRoll } from "~/ui/camera-roll"
import { alertSuccess, alertError, alertWarning } from "~utils/alert"
import { downloadFileInBrowser } from "~utils/h5-download"

export type PSaveAsset = {
  uri: string
}

const { UNDETERMINED, GRANTED, DENIED } = MediaLibrary.PermissionStatus

const storageName = Platform.OS === "ios" ? "相簿" : "硬盘"

/** 从远程 URL 推断缓存文件扩展名（大小写不敏感），避免 .MOV 被误判成 .png */
function inferRemoteFileExtension(remoteUri: string): string {
  const pathOnly = remoteUri.split("?")[0].split("#")[0].toLowerCase()
  if (pathOnly.endsWith(".mov")) return ".mov"
  if (pathOnly.endsWith(".mp4")) return ".mp4"
  if (pathOnly.endsWith(".m4v")) return ".m4v"
  if (pathOnly.endsWith(".webm")) return ".webm"
  if (pathOnly.endsWith(".avi")) return ".avi"
  if (pathOnly.endsWith(".mkv")) return ".mkv"
  if (pathOnly.endsWith(".3gp")) return ".3gp"
  if (pathOnly.endsWith(".jpg") || pathOnly.endsWith(".jpeg")) return ".jpg"
  if (pathOnly.endsWith(".png")) return ".png"
  if (pathOnly.endsWith(".gif")) return ".gif"
  if (pathOnly.endsWith(".webp")) return ".webp"
  if (pathOnly.endsWith(".heic") || pathOnly.endsWith(".heif")) return ".heic"
  return ".png"
}

function isMissingMediaLibraryWritePermissionError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e ?? "")
  return /Missing MEDIA_LIBRARY write permission/i.test(msg)
}

/**
 * 通过文件 uri 推断保存到相册时的媒体类型。
 * 华为/鸿蒙等机型走 CameraRoll 兜底时需要用到。
 */
function inferAssetType(uri: string): "photo" | "video" {
  const lower = uri.split("?")[0].split("#")[0].toLowerCase()
  if (
    lower.endsWith(".mp4") ||
    lower.endsWith(".mov") ||
    lower.endsWith(".m4v") ||
    lower.endsWith(".webm") ||
    lower.endsWith(".avi") ||
    lower.endsWith(".mkv") ||
    lower.endsWith(".3gp")
  ) {
    return "video"
  }
  return "photo"
}

/**
 * Android Huawei/HarmonyOS 上 expo-media-library 的写权限校验经常误判，
 * 这里改用 @react-native-camera-roll/camera-roll，
 * 它在 Android Q+ 直接通过 MediaStore ContentResolver 写入，
 * 不依赖 WRITE_EXTERNAL_STORAGE，能绕开这套校验。
 */
async function saveViaCameraRoll(uri: string) {
  const normalized = uri.startsWith("/") ? `file://${uri}` : uri
  return CameraRoll.saveAsset(normalized, { type: inferAssetType(normalized) })
}

const useMediaLibrary = ({ writeOnly, requestPermission = true }) => {
  const [inited, setInited] = useState(false)
  const [permission, setPermission] = useState(undefined)

  /** 判断是否是本地路径 */
  const isLocalFile = (uri: string) => 
    uri.startsWith("file://") || uri.startsWith("/") || uri.startsWith("ph://");

  /** 自动下载远程 URL -> file:// */
  const downloadIfNeeded = async (uri: string) => {
    if (isLocalFile(uri)) {
      // 如果是绝对路径但没加 file://，FileSystem 某些 API 会报错，统一加上
      return uri.startsWith("/") ? `file://${uri}` : uri;
    }

    const extension = inferRemoteFileExtension(uri)
    const fileUri = `${FileSystem.cacheDirectory}media_${Date.now()}${extension}`

    const res = await FileSystem.downloadAsync(uri, fileUri)

    if (!res?.uri) {
      throw new Error("文件下载失败")
    }
    const info = await FileSystem.getInfoAsync(res.uri)
    if (!info.exists || (info.size != null && info.size <= 0)) {
      throw new Error("文件下载失败或文件为空")
    }

    return res.uri
  }


  async function base64ToFile(base64: string, ext = "jpg") {
    const fileUri = FileSystem.cacheDirectory + `media_${Date.now()}.${ext}`

    await FileSystem.writeAsStringAsync(fileUri, base64, {
      encoding: FileSystem.EncodingType.Base64,
    })

    return fileUri
  }

  const canAskAgain = useMemo(() => {
    return permission?.canAskAgain
  }, [permission])

  const isGranted = useMemo(() => {
    return permission?.status === GRANTED
  }, [permission])

  const getPermissionsAsync = useCallback(async () => {
    const permission = await MediaLibrary.getPermissionsAsync(writeOnly)
    setPermission(permission)
    return permission
  }, [writeOnly])
  
  const requestPermissionsAsync = useCallback(async () => {
    const response = await MediaLibrary.requestPermissionsAsync(writeOnly)
    setPermission(response)
    return response
  }, [writeOnly])
  /** 检查/请求权限（Callback 形式） */
  const requestPermissionsAsyncWithCallback = useCallback(
    async ({ callback, promptToSetting = true }) => {
      const current = permission ?? (await MediaLibrary.getPermissionsAsync(writeOnly))
      const response =
        current.status === UNDETERMINED
          ? await MediaLibrary.requestPermissionsAsync(writeOnly)
          : current
      const { status } = response
      switch (status) {
        case GRANTED:
          return callback()
        case DENIED:
          if (promptToSetting) alertManualChangeSetting()
          else alertWarning({ message: `请至设定开启${storageName}相关权限` })
          break
        default:
          break
      }
      setPermission(response)
      return response
    },
    [permission, writeOnly],
  )
   /** 保存文件 */
  const saveAsset = useCallback(async (input: { uri: string } | null | undefined) => {
    try {
      const uri = input?.uri
      if (!uri) {
        alertWarning({ message: "保存失败：文件地址为空" })
        return false
      }
      const res = await MediaLibrary.saveToLibraryAsync(uri)
      alertSuccess({ message: "保存成功!" })
      return res
    } catch (e) {
      alertError({ message: e.message })
      return e
    }
  }, [])

  const alertManualChangeSetting = useCallback(() => {
    Alert.alert(`前往设定打开${storageName}存取权限?`, "", [
      {
        text: "否",
        style: "destructive",
      },
      {
        text: "是",
        style: "default",
        onClick: () => {
          if (Platform.OS === "ios") {
            Linking.openURL("app-settings:")
          } else {
            const pkg = Application.applicationId
            IntentLauncher.startActivityAsync(
              IntentLauncher.ActivityAction.APPLICATION_DETAILS_SETTINGS,
              {
                data: "package:" + pkg,
              },
            )
          }
        },
      },
    ])
  }, [])

  /** 检查/请求权限（Promise 形式） */
  const ensurePermission = useCallback(async () => {
    try {
      // 先读取当前状态，避免某些 ROM 上 request 结果不稳定
      const current = permission ?? (await MediaLibrary.getPermissionsAsync(writeOnly))
      let perm = current

      // 未决定，或已拒绝但仍可再次申请：主动触发系统弹窗
      if (current.status === UNDETERMINED || (current.status === DENIED && current.canAskAgain)) {
        perm = await MediaLibrary.requestPermissionsAsync(writeOnly)
      }

      setPermission(perm)

      if (perm.status === MediaLibrary.PermissionStatus.GRANTED) {
        return true
      }

      alertManualChangeSetting()
      return false
    } catch (e) {
      alertError({ message: e.message })
      return false
    }
  }, [permission, writeOnly])

  /** 保存文件 */
const saveAssetAsync = useCallback(
  async ({ uri }: { uri: string }) => {
    try {
      if (!uri) {
        alertWarning({ message: "保存失败：文件地址为空" })
        return false
      }

      // H5：直接触发浏览器下载，不走相册 API
      if (Platform.OS === "web") {
        const httpUrl = uri.startsWith("http") ? uri : uri
        await downloadFileInBrowser(httpUrl)
        alertSuccess({ message: "已开始下载" })
        return true
      }

      const ok = await ensurePermission()
      if (!ok) return false

      let finalUri = uri

      // base64 (data:xxx;base64 或纯 base64)
      if (uri.startsWith("data:") || /^[A-Za-z0-9+/=]+$/.test(uri)) {
        const ext =
          uri.startsWith("data:video") ? "mp4" :
          uri.startsWith("data:image/png") ? "png" :
          uri.startsWith("data:image") ? "jpg" :
          "jpg"

        const base64Data = uri.startsWith("data:")
          ? uri.split(",")[1]
          : uri

        finalUri = await base64ToFile(base64Data, ext)
      }

      // remote url (https)
      else if (!uri.startsWith("file://")) {
        finalUri = await downloadIfNeeded(uri)
      }

      // 保存
      let asset
      try {
        asset = await MediaLibrary.saveToLibraryAsync(finalUri)
      } catch (e) {
        // 华为/鸿蒙等机型上偶发：权限接口返回 granted，
        // 但 saveToLibraryAsync / createAssetAsync 仍报缺少写权限
        // (expo-media-library 在 Android < 13 上硬要 WRITE_EXTERNAL_STORAGE，
        //  鸿蒙的权限对话框并不会真的把它授给应用)。
        if (isMissingMediaLibraryWritePermissionError(e) && Platform.OS === "android") {
          try {
            asset = await saveViaCameraRoll(finalUri)
          } catch (fallbackErr) {
            throw fallbackErr instanceof Error ? fallbackErr : e
          }
        } else if (isMissingMediaLibraryWritePermissionError(e)) {
          // iOS 走原有再请求权限的流程
          const status = await MediaLibrary.requestPermissionsAsync(true)
          if (status.granted) {
            asset = await MediaLibrary.createAssetAsync(finalUri)
          } else {
            throw e
          }
        } else {
          throw e
        }
      }
      alertSuccess({ message: "保存成功!" })
      return asset
    } catch (e) {
      alertError({ message: e.message })
      return false
    }
  },
  [ensurePermission]
)


useEffect(() => {
  if (!inited && requestPermission) {
    ;(async () => {
      const perm = await MediaLibrary.requestPermissionsAsync(writeOnly)
      setPermission(perm)
      setInited(true)
    })()
  }
}, [inited, writeOnly])

  return {
    permission,
    canAskAgain,
    isGranted,
    getPermissionsAsync,
    requestPermissionsAsync,
    requestPermissionsAsyncWithCallback,
    saveAsset,
    saveAssetAsync,
    alertManualChangeSetting,
  }
}

export default useMediaLibrary
